/**
 * InstituteAutomationService — FRONTEND DATA SERVICE for the Automated Report
 * Module. Talks to the `admin-master-reports` microservice (spec §10).
 * Auth token is attached automatically by axiosClient.
 *
 * This slice is read-only: report catalog + a report's sections/components.
 */
import { axiosClient } from '../../../library/Service/axiosClient';

const BASE = () => `${process.env.REACT_APP_ADMIN_MASTER_REPORTS_SERVER}/reports/module`;

export interface ApiComponent {
    id: number;
    code: string;
    name: string;
    description: string;
    valueType: 'numeric' | 'text' | 'percentage';
    unit: string | null;
    generatorKey: string;
    displayOrder: number;
    renderStyle: string;
    // Per-component display config (Global Settings). Nullable until seeded.
    frequencyMonths: number | null;
    frequencyLabel: string | null;
    minThreshold: string | null;
    applicability: 'both' | 'k12' | 'college' | null;
    priority: 'high' | 'medium' | 'low' | null;
}

export interface ApiSection {
    id: number;
    code: string;
    name: string;
    displayOrder: number;
    components: ApiComponent[];
}

export interface ApiDefinition {
    id: number;
    code: string;
    name: string;
    description: string;
    category: string;
    defaultAnchorDay: number;
}

// ── Global Settings tab (GET /components) — editable per-component config ──
export interface ApiGlobalComponent {
    id: number;
    code: string;
    name: string;
    description: string;
    valueType: 'numeric' | 'text' | 'percentage';
    unit: string | null;
    renderStyle: string;
    displayOrder: number;
    frequencyMonths: number | null;
    frequencyLabel: string | null;
    thresholdType: 'always' | 'min_value' | 'module_enabled' | null;
    minValue: string | null;
    requiredModuleCode: string | null;
    audience: 'both' | 'k12' | 'degree' | null;
    priority: 'high' | 'medium' | 'low' | null;
    isActive: boolean;
}
export interface ApiGlobalSection {
    id: number;
    code: string;
    name: string;
    displayOrder: number;
    frequencyMonths: number; // 0 = Skip
    frequencyLabel: string | null;
    components: ApiGlobalComponent[];
}
export interface ApiGlobalConfig {
    definition: { id: number; code: string; name: string; description: string };
    sections: ApiGlobalSection[];
}
/** Fields accepted by PUT /components/:id (all optional; only changed ones sent). */
export interface ComponentPatch {
    default_frequency_months?: number;
    threshold_type?: 'always' | 'min_value' | 'module_enabled';
    default_min_value?: number | null;
    priority?: 'high' | 'medium' | 'low';
    is_active?: boolean;
}

// ── Institute picker (reuses the internal-admin-reports institute search) ──
const INSTITUTE_SEARCH = () => `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/institute/get-institute-details`;

export interface InstituteRow { id: string; name: string; city?: string; type?: string }

// ── Live preview (read-only generator output, no run created) ──
export interface LivePreviewComponent {
    id: number; code: string; name: string; valueType: 'numeric' | 'text' | 'percentage';
    unit: string | null; renderStyle: string; value: number | string | null;
    meta: { comparison_previous_period?: number; unit?: string; proxy?: string; error?: string } | null;
    generated: boolean;
}
export interface LivePreviewSection { id: number; code: string; name: string; components: LivePreviewComponent[] }
export interface LivePreview {
    definition: { id: number; code: string; name: string; description: string; periodStart: string; periodEnd: string };
    sections: LivePreviewSection[];
}

// ── Saved run detail (getRun) — effective = override ?? generated ──
export interface RunComponent {
    componentId: number;
    code: string;
    name: string;
    valueType: 'numeric' | 'text' | 'percentage';
    unit: string | null;
    renderStyle: string;
    generated: { numeric: string | null; text: string | null; meta: unknown };
    override: { numeric: string | null; text: string | null; reason: string | null; at: string } | null;
    effective: { numeric: string | null; text: string | null };
    isIncluded: boolean;
    exclusionReason: string | null;
}
export interface RunSection { id: number; code: string; name: string; displayOrder: number; components: RunComponent[] }
export interface RunDetail {
    run: {
        id: number; instituteId: string; reportDefinitionId: number; reportName: string; reportCode: string;
        description: string; category: string; scheduledSendDate: string; generatedAt: string | null;
        status: string; approvedAt: string | null; sentAt: string | null; deliveryStatus: string | null; recipientEmail: string | null;
    };
    sections: RunSection[];
}
export interface CurrentRun { id: number; status: string; scheduled_send_date: string }

// ── Overview stats + scheduling ──
export interface OverviewStats { reportsScheduled: number; sentThisMonth: number; pendingReview: number; activeOverrides: number; componentCount: number; clientOverrideInstitutes: number }
export type OverviewListType = 'scheduled' | 'sent' | 'pending' | 'overrides';
export interface OverviewListItem { id: string; instituteId: string; title: string; subtitle: string; frequencyLabel?: string; nextSend?: string }

// ── Email delivery (Send Now) ──
export interface RunRecipient { email: string; adminHash: string | null; ssoUrl: string }
export interface SendResult { ok: boolean; sent: number; total: number; results: { email: string; ok: boolean; error?: string }[] }

// ── Institute-facing web view (read-only, own institute only) ──
export interface InstituteReportComponent { name: string; valueType: 'numeric' | 'text' | 'percentage'; unit: string | null; renderStyle: string; value: string | number | null }
export interface InstituteReportSection { id: number; name: string; components: InstituteReportComponent[] }
export interface InstituteReport {
    institute: { id: string; name: string | null };
    report: { name: string; scheduledSendDate: string; status: string; sentAt: string | null };
    sections: InstituteReportSection[];
}

// ── Client Settings — merged per-component override rows (spec §7 D3) ──
export interface MergedOverrideRow {
    componentId: number;
    code: string;
    name: string;
    section: string;
    global: { frequencyMonths: number; minValue: string | null; thresholdType: string; audience: string | null; priority: string | null };
    override: { customFrequencyMonths: number | null; includeInReport: 'global' | 'yes' | 'no'; customMinValue: string | null; smNotes: string | null } | null;
    effective: { frequencyMonths: number; minValue: string | null; includeInReport: string };
    isClientOverride: boolean;
}
export interface SectionOverrideRow {
    sectionId: number;
    code: string;
    name: string;
    globalFrequencyMonths: number;
    customFrequencyMonths: number | null;
    effectiveFrequencyMonths: number;
    isOverride: boolean;
}
export interface OverrideUpsertBody {
    custom_frequency_months?: number | null;
    include_in_report?: 'global' | 'yes' | 'no';
    custom_min_value?: number | null;
    sm_notes?: string | null;
}

// ── Audit log (raw rows from report_audit_log, §10.8) ──
export interface AuditRow {
    id: number;
    action: string;
    actor_id: string | null;
    actor_first_name: string | null;
    actor_last_name: string | null;
    actor_role: string | null;
    institute_id: string | null;
    institute_name: string | null;
    report_definition_id: number | null;
    report_run_id: number | null;
    report_component_id: number | null;
    before_value: unknown;
    after_value: unknown;
    reason: string | null;
    performed_at: string;
}

export const InstituteAutomationService = {
    /** §10.1 — the report catalog. */
    getDefinitions: async (): Promise<ApiDefinition[]> => {
        const res = await axiosClient().get(`${BASE()}/definitions`);
        return res?.data?.data ?? [];
    },

    /** §10.1 — a report's sections + nested components. */
    getSections: async (definitionId: number): Promise<{ definition: ApiDefinition; sections: ApiSection[] }> => {
        const res = await axiosClient().get(`${BASE()}/definitions/${definitionId}/sections`);
        return res?.data?.data;
    },

    /** Global Settings tab — sections + components with editable global config. */
    getComponents: async (): Promise<ApiGlobalConfig> => {
        const res = await axiosClient().get(`${BASE()}/components`);
        return res?.data?.data;
    },

    /** PUT /components/:id — save changed global settings for one component. */
    updateComponent: async (id: number, patch: ComponentPatch): Promise<void> => {
        await axiosClient().put(`${BASE()}/components/${id}`, patch);
    },
    /** PUT /sections/:id/frequency — set a section's send frequency (0 = Skip) for all clients. */
    updateSectionFrequency: async (sectionId: number, frequencyMonths: number): Promise<void> => {
        await axiosClient().put(`${BASE()}/sections/${sectionId}/frequency`, { frequency_months: frequencyMonths });
    },

    /** GET /overview/stats — the 4 Overview stat-card counts. */
    getOverviewStats: async (): Promise<OverviewStats> => {
        const res = await axiosClient().get(`${BASE()}/overview/stats`);
        return res?.data?.data;
    },

    /** GET /global/frequency — the single report-level send frequency. */
    getGlobalFrequency: async (): Promise<{ frequencyMonths: number }> => {
        const res = await axiosClient().get(`${BASE()}/global/frequency`);
        return res?.data?.data;
    },
    /** PUT /global/frequency — set it + propagate to every institute's schedule. */
    setGlobalFrequency: async (frequencyMonths: number): Promise<{ frequencyMonths: number; appliedToSchedules: number; nextSendDate: string; persisted: boolean }> => {
        const res = await axiosClient().put(`${BASE()}/global/frequency`, { frequency_months: frequencyMonths });
        return res?.data?.data;
    },

    /** GET /overview/list/:type — the drill-down list behind a stat card. */
    getOverviewList: async (type: OverviewListType): Promise<OverviewListItem[]> => {
        const res = await axiosClient().get(`${BASE()}/overview/list/${type}`);
        return res?.data?.data ?? [];
    },

    /** POST /schedule/enable — schedule an institute's report. Pass sendDate (YYYY-MM-DD, future) for a specific date; else 1st-of-period. */
    enableSchedule: async (instituteId: string | number, frequencyMonths: number, sendDate?: string): Promise<{ nextSendDate: string; nextGenerationDate: string; frequencyMonths: number }> => {
        const res = await axiosClient().post(`${BASE()}/schedule/enable`, {
            institute_id: instituteId, frequency_months: frequencyMonths, ...(sendDate ? { send_date: sendDate } : {}),
        });
        return res?.data?.data;
    },

    /** Search institutes by name (reuses institute/get-institute-details). Returns up to `limit`. */
    searchInstitutes: async (name = '', limit = 20): Promise<InstituteRow[]> => {
        const res = await axiosClient().post(INSTITUTE_SEARCH(), {
            name, clientType: 'CLIENT', instituteGroupId: [], instituteCountryCode: [], instituteCityCode: [],
            instituteTypeCode: [], createdDateRange: [], hasActive: [],
        });
        const rows: any[] = res?.data?.data ?? res?.data ?? [];
        return rows.slice(0, limit).map((r) => ({
            id: String(r.id),
            name: r.name ?? r.displayName ?? '',
            city: r.cityCode ?? undefined,
            type: r.instituteTypeCode ?? r.clientType ?? undefined,
        }));
    },

    /** Read-only live preview: runs the report's generators for an institute. Creates no run. */
    livePreview: async (instituteId: string | number, reportId?: number): Promise<LivePreview> => {
        const res = await axiosClient().get(`${BASE()}/preview/live`, {
            params: { institute_id: instituteId, ...(reportId ? { report_id: reportId } : {}) },
        });
        return res?.data?.data;
    },

    // ── Client Settings overrides ──
    /** GET /institutes/:id/section-overrides — per-section frequency (global + client override). */
    getSectionOverrides: async (instituteId: string | number): Promise<SectionOverrideRow[]> => {
        const res = await axiosClient().get(`${BASE()}/institutes/${instituteId}/section-overrides`);
        return res?.data?.data ?? [];
    },
    /** PUT /institutes/:id/section-overrides/:sectionId — set (freq) or clear (null) a section override. */
    upsertSectionOverride: async (instituteId: string | number, sectionId: number, freq: number | null): Promise<void> => {
        await axiosClient().put(`${BASE()}/institutes/${instituteId}/section-overrides/${sectionId}`, { custom_frequency_months: freq });
    },

    /** GET /institutes/:id/overrides — merged component rows (global + override). */
    getInstituteOverrides: async (instituteId: string | number): Promise<MergedOverrideRow[]> => {
        const res = await axiosClient().get(`${BASE()}/institutes/${instituteId}/overrides`);
        return res?.data?.data ?? [];
    },
    /** PUT /institutes/:id/overrides/:componentId — create/update one override. */
    upsertOverride: async (instituteId: string | number, componentId: number, body: OverrideUpsertBody): Promise<void> => {
        await axiosClient().put(`${BASE()}/institutes/${instituteId}/overrides/${componentId}`, body);
    },
    /** DELETE /institutes/:id/overrides/:componentId — remove one override. */
    removeOverride: async (instituteId: string | number, componentId: number): Promise<void> => {
        await axiosClient().delete(`${BASE()}/institutes/${instituteId}/overrides/${componentId}`);
    },
    /** POST /institutes/:id/overrides/reset — reset all overrides to global. */
    resetOverrides: async (instituteId: string | number): Promise<void> => {
        await axiosClient().post(`${BASE()}/institutes/${instituteId}/overrides/reset`);
    },

    /** GET /institute-view/latest — the logged-in institute's OWN latest report (read-only). */
    getMyLatestReport: async (): Promise<InstituteReport | null> => {
        try {
            const res = await axiosClient().get(`${BASE()}/institute-view/latest`);
            return res?.data?.data ?? null;
        } catch (e: unknown) {
            // axiosClient's response interceptor rejects with a FLATTENED shape
            // ({ isError, message, status, data }) — so status is top-level, not e.response.status.
            const err = e as { status?: number; response?: { status?: number } };
            if ((err?.status ?? err?.response?.status) === 404) return null; // 404 = no report yet, not an error
            throw e;
        }
    },

    /** GET /audit-log — module audit trail (raw rows). */
    getAuditLog: async (params: { filter?: string; run_id?: number; from?: string; to?: string; page?: number } = {}): Promise<AuditRow[]> => {
        const res = await axiosClient().get(`${BASE()}/audit-log`, { params });
        return res?.data?.data ?? [];
    },

    // ── Report runs (Preview & Send persistence) ──
    /** GET /runs/current — the current active run for an institute's report (or null). */
    getCurrentRun: async (instituteId: string | number, reportId: number): Promise<CurrentRun | null> => {
        const res = await axiosClient().get(`${BASE()}/runs/current`, { params: { institute_id: instituteId, report_id: reportId } });
        return res?.data?.data ?? null;
    },
    /** GET /runs/:runId — full run detail with effective values. */
    getRun: async (runId: number): Promise<RunDetail> => {
        const res = await axiosClient().get(`${BASE()}/runs/${runId}`);
        return res?.data?.data;
    },
    /** POST /runs/generate — create (or reuse) a report run for an institute. */
    generateRun: async (instituteId: string | number, reportDefinitionId: number, scheduledSendDate?: string): Promise<{ runId: number; status: string }> => {
        const res = await axiosClient().post(`${BASE()}/runs/generate`, {
            institute_id: instituteId, report_definition_id: reportDefinitionId,
            ...(scheduledSendDate ? { scheduled_send_date: scheduledSendDate } : {}),
        });
        return res?.data?.data;
    },
    /** PUT /runs/:runId/components/:componentId/override — set an SM value override on a run. */
    overrideRunComponent: async (runId: number, componentId: number, body: { numeric?: number | null; text?: string | null; reason?: string }): Promise<void> => {
        await axiosClient().put(`${BASE()}/runs/${runId}/components/${componentId}/override`, {
            override_numeric_value: body.numeric ?? null,
            override_text_value: body.text ?? null,
            override_reason: body.reason ?? null,
        });
    },
    /** POST /runs/:runId/approve — approve a run for sending. */
    approveRun: async (runId: number, notes?: string): Promise<void> => {
        await axiosClient().post(`${BASE()}/runs/${runId}/approve`, { approval_notes: notes ?? null });
    },
    /** GET /runs/:runId/recipients — institute-admin emails (+ SSO links) for this run's institute. */
    getRunRecipients: async (runId: number): Promise<RunRecipient[]> => {
        const res = await axiosClient().get(`${BASE()}/runs/${runId}/recipients`);
        return res?.data?.data ?? [];
    },
    /** POST /runs/:runId/send — email the report to the chosen addresses. */
    sendRun: async (runId: number, emails: string[]): Promise<SendResult> => {
        const res = await axiosClient().post(`${BASE()}/runs/${runId}/send`, { emails });
        return res?.data?.data;
    },
};
